export type AiModelOptionResponse = {
    id: string;
    name: string;
    description: string | null;
    contextLength: number | null;
    promptPricePerMillion: number | null;
    completionPricePerMillion: number | null;
    supportsStructuredOutputs: boolean;
    recommended: boolean;
    recommendedReason: string | null;
};

export type AiModelsCatalogResponse = {
    defaultModelId: string;
    models: AiModelOptionResponse[];
};

export type AiModelOptionProps = AiModelOptionResponse;

export class AiModelOptionModel {
    private constructor(private readonly props: AiModelOptionProps) {}

    public static with(props: AiModelOptionProps): AiModelOptionModel {
        return new AiModelOptionModel(props);
    }

    public static withFromResponse(response: AiModelOptionResponse): AiModelOptionModel {
        return AiModelOptionModel.with({ ...response });
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get description(): string | null {
        return this.props.description;
    }

    public get contextLength(): number | null {
        return this.props.contextLength;
    }

    public get promptPricePerMillion(): number | null {
        return this.props.promptPricePerMillion;
    }

    public get completionPricePerMillion(): number | null {
        return this.props.completionPricePerMillion;
    }

    public get supportsStructuredOutputs(): boolean {
        return this.props.supportsStructuredOutputs;
    }

    public get recommended(): boolean {
        return this.props.recommended;
    }

    public get recommendedReason(): string | null {
        return this.props.recommendedReason;
    }

    public toObject(): AiModelOptionProps {
        return { ...this.props };
    }
}

export type AiModelsCatalogProps = {
    defaultModelId: string;
    models: AiModelOptionModel[];
};

export class AiModelsCatalogModel {
    private constructor(private readonly props: AiModelsCatalogProps) {}

    public static with(props: AiModelsCatalogProps): AiModelsCatalogModel {
        return new AiModelsCatalogModel(props);
    }

    public static withFromResponse(response: AiModelsCatalogResponse): AiModelsCatalogModel {
        return AiModelsCatalogModel.with({
            defaultModelId: response.defaultModelId,
            models: response.models.map((model) => AiModelOptionModel.withFromResponse(model))
        });
    }

    public get defaultModelId(): string {
        return this.props.defaultModelId;
    }

    public get models(): AiModelOptionModel[] {
        return this.props.models;
    }

    public findById(id: string): AiModelOptionModel | null {
        return this.props.models.find((model) => model.id === id) ?? null;
    }

    public resolveLabel(id: string | null | undefined): string {
        if (!id) {
            return '—';
        }
        return this.findById(id)?.name ?? id;
    }
}
